import { Button } from '@/components/ui/button';
import * as Checkbox from '@/components/ui/checkbox';
import * as Field from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import * as RadioGroup from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import { toaster } from '@/components/ui/toast';
import { DataCombobox } from '@components/DataCombobox';
import { Constants } from '@hvf/contracts';
import { createForm, setValue, valiForm } from '@modular-forms/solid';
import { userId } from '@state/auth';
import { useMutation } from '@tanstack/solid-query';
import { CheckIcon } from 'lucide-solid';
import { For, Show, createEffect } from 'solid-js';
import { useService } from 'solid-services';
import { queryClient } from 'src/App';
import { Flex, Grid } from 'styled-system/jsx';
import { grid } from 'styled-system/patterns';
import { useState } from '../state';
import { fetchEvents, fetchGroups, fetchProfiles, saveRecording, updateRecording } from './api';
import { type RecordingForm, RecordingSchema } from './recordingSchema';

export default function (props: { onSuccess: () => void }) {
  const { editRecording, setEditRecording } = useService(useState)();
  const isEdit = () => !!editRecording();

  // 1. Setup Mutation
  const mutation = useMutation(() => ({
    mutationFn: (values: RecordingForm) => {
      const existing = editRecording();
      if (existing) {
        return updateRecording({ id: existing.id, values });
      }
      return saveRecording(values);
    },
    onSuccess: () => {
      toaster.create({
        title: isEdit() ? 'Recording updated' : 'Recording created',
        type: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['recordings-groups', userId()] });
      setEditRecording(undefined); // Clear state after success
      props.onSuccess();
    },
    onError: (error) => {
      toaster.create({
        title: 'Error creating recording',
        description: error.message,
        type: 'error',
      })
    },
  }));

  // 2. Setup Form
  const [recordingForm, { Form, Field: FormField }] = createForm<RecordingForm>({
    validate: valiForm(RecordingSchema),
    initialValues: {
      title: editRecording()?.title ?? '',
      event_id: editRecording()?.event_id ?? null,
      provider_id: editRecording()?.provider_id ?? '',
      description: editRecording()?.description ?? '',
      evergreen: editRecording()?.evergreen ?? undefined,
      hosted_by: editRecording()?.hosted_by ?? null,
      group: editRecording()?.group ?? null,
      provider: editRecording()?.provider ?? 'vimeo',
      occurrence_date: editRecording()?.occurrence_date ?? new Date().toISOString().split('T')[0],
    }
  });

  const handleSubmit = (values: RecordingForm) => {
    mutation.mutate(values);
  };

  createEffect(() => {
    console.log(editRecording())
  })

  return (
    <Form onSubmit={handleSubmit} class={grid({ gap: '3', w: 'full' })}>

      {/* Recording URL */}
      <FormField name="title">
        {(field, props) => (
          <Field.Root invalid={!!field.error}>
            <Field.Label>Title</Field.Label>
            <Input
              {...props}
              type="text"
              value={field.value ?? ''}
            />
            <Field.ErrorText>{field.error}</Field.ErrorText>
          </Field.Root>
        )}
      </FormField>

      <FormField name="description">
        {(field, props) => (
          <Field.Root invalid={!!field.error}>
            <Field.Label>Description</Field.Label>
            <Textarea
              {...props}
              value={field.value ?? ''}
            />
            <Field.ErrorText>{field.error}</Field.ErrorText>
          </Field.Root>
        )}
      </FormField>

      <Grid gridTemplateColumns='1fr' gap='3'>

        <FormField name="evergreen" type="boolean">
          {(field, props) => (
            <Field.Root invalid={!!field.error}>
              <Checkbox.Root
                colorPalette="jade"
                checked={field.value}
                onCheckedChange={(details) =>
                  setValue(recordingForm, 'evergreen', !!details.checked)
                }
              >
                <Checkbox.Control>
                  <Checkbox.Indicator>
                    <CheckIcon />
                  </Checkbox.Indicator>
                </Checkbox.Control>
                <Checkbox.Label>Evergreen</Checkbox.Label>

                <Checkbox.HiddenInput {...props} />
              </Checkbox.Root>
              <Field.ErrorText>{field.error}</Field.ErrorText>
            </Field.Root>
          )}
        </FormField>

        <FormField name="provider">
          {(field) => (
            <Field.Root invalid={!!field.error}>
              <Field.Label>Video Provider</Field.Label>
              <RadioGroup.Root
                colorPalette="jade"
                value={field.value}
                onValueChange={(details) => setValue(recordingForm, 'provider', details.value as any)}
              >
                <For each={Constants.public.Enums.video_providers}>
                  {(provider) => (
                    <RadioGroup.Item value={provider}>
                      <RadioGroup.ItemHiddenInput />
                      <RadioGroup.ItemControl />
                      <RadioGroup.ItemText>{provider}</RadioGroup.ItemText>
                    </RadioGroup.Item>
                  )}
                </For>
              </RadioGroup.Root>
              <Field.ErrorText>{field.error}</Field.ErrorText>
            </Field.Root>
          )}
        </FormField>

      </Grid>

      {/* Recording URL */}
      <FormField name="provider_id">
        {(field, props) => (
          <Field.Root invalid={!!field.error}>
            <Field.Label>Video Id</Field.Label>
            <Input
              {...props}
              type="text"
              value={field.value ?? ''}
            />
            <Field.ErrorText>{field.error}</Field.ErrorText>
          </Field.Root>
        )}
      </FormField>

      {/* Occurrence Date */}
      <FormField name="occurrence_date">
        {(field, props) => (
          <Field.Root invalid={!!field.error}>
            <Field.Label>Date of Occurrence</Field.Label>
            <Input
              {...props}
              type="date"
              value={field.value ?? ''}
            />
            <Field.ErrorText>{field.error}</Field.ErrorText>
          </Field.Root>
        )}
      </FormField>

      {/* Group Field: Acts as Text Input + Suggestions */}
      <FormField name="group">
        {(field) => (
          <Field.Root invalid={!!field.error}>
            <DataCombobox
              label='Group'
              placeholder='Search groups or type a new one...'
              queryKey='groups'
              fetchFn={fetchGroups}
              value={field.value}
              initialItem={{
                label: editRecording()?.group ?? '',
                value: editRecording()?.group ?? ''
              }}
              onValueChange={(val) => {
                setValue(recordingForm, 'group', val)
              }}
              error={field.error}
              allowCustomValue={true} // <-- Enables free-text input
            />
            <Field.HelperText>
              leave blank for ad-hoc recordings.
            </Field.HelperText>
            <Field.ErrorText>{field.error}</Field.ErrorText>
          </Field.Root>
        )}
      </FormField>

      {/* Event Field: Acts strictly as an Async Select */}
      <FormField name="event_id">
        {(field) => (
          <Field.Root invalid={!!field.error}>
            <DataCombobox
              label='Event'
              placeholder='Search events...'
              queryKey='events'
              fetchFn={fetchEvents}
              value={field.value}
              initialItem={{
                label: editRecording()?.events?.group ?? '',
                value: editRecording()?.event_id ?? ''
              }}
              onValueChange={(val) => {
                setValue(recordingForm, 'event_id', val)
              }}
              error={field.error}
            // Inherits default allowCustomValue={false}
            />
            <Show when={field.value} fallback={<Field.HelperText>leave blank for ad-hoc recordings.</Field.HelperText>}>
              <Field.HelperText>
                This recording will automatically inherit the Event's category.
              </Field.HelperText>
            </Show>
            <Field.ErrorText>{field.error}</Field.ErrorText>
          </Field.Root>
        )}
      </FormField>

      {/* Hosted By Field: Links to public.profile */}
      <FormField name="hosted_by">
        {(field) => (
          <Field.Root invalid={!!field.error}>
            <DataCombobox
              label='Hosted By'
              placeholder='Search for a host...'
              queryKey='profiles'
              fetchFn={fetchProfiles}
              initialItem={{
                label: editRecording()?.host?.username ?? '',
                value: editRecording()?.host?.id ?? ''
              }}
              value={field.value}
              onValueChange={(val) => {
                setValue(recordingForm, 'hosted_by', val)
              }}
              error={field.error}
              // We set this to false because the ID must exist in the profile table
              allowCustomValue={false}
            />
            <Field.HelperText>
              Select the user who hosted this session.
            </Field.HelperText>
            <Field.ErrorText>{field.error}</Field.ErrorText>
          </Field.Root>
        )}
      </FormField>

      <Button
        type="submit"
        loading={mutation.isPending}
        colorPalette="jade"
        variant="solid"
        justifySelf="start"
      >
        {isEdit() ? 'Update Recording' : 'Create Recording'}
      </Button>
    </Form>
  );
}
